/**
 * Edit/Create Flight Modal Component
 * Reusable modal for creating and editing flight details
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFlightsStore, Flight, FlightStatus } from '../store/flights';
import { useFlightAttributesStore } from '../store/flightAttributes';
import { ColorPicker } from './ColorPicker';

interface EditFlightModalProps {
  flight?: Flight | null; // null for create mode
  projectId?: string; // required for create mode
  onClose: () => void;
  onCreated?: (flight: Flight) => void;
  onUpdated?: () => void;
}

export function EditFlightModal({ flight, projectId, onClose, onCreated, onUpdated }: EditFlightModalProps) {
  const { t } = useTranslation('flights');
  const { createFlight, updateFlight } = useFlightsStore();
  const { attributes, fetchAttributes, setFlightAttributes, getFlightAttributes } = useFlightAttributesStore();
  const isCreateMode = !flight;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedAttributeIds, setSelectedAttributeIds] = useState<string[]>([]);
  const [isLoadingAttributes, setIsLoadingAttributes] = useState(false);

  // Default dates: today to 30 days from now
  const today = new Date().toISOString().split('T')[0];
  const thirtyDaysLater = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const [formData, setFormData] = useState({
    name: flight?.name || '',
    description: flight?.description || '',
    start_date: flight?.start_date?.split('T')[0] || today,
    end_date: flight?.end_date?.split('T')[0] || thirtyDaysLater,
    status: flight?.status || 'draft' as FlightStatus,
    brand_voice: flight?.ai_context?.brand_voice || '',
    target_audience: flight?.ai_context?.target_audience || '',
    key_messages: flight?.ai_context?.key_messages?.join(', ') || '',
    hashtags: flight?.ai_context?.hashtag_strategy?.join(' ') || '',
    post_count: flight?.goals?.post_count?.toString() || '',
    platforms: flight?.goals?.platforms || ['facebook', 'instagram'],
    color: flight?.color || '#3B82F6',
  });

  // Load attributes for project
  useEffect(() => {
    const effectiveProjectId = projectId || flight?.project_id;
    if (effectiveProjectId) {
      setIsLoadingAttributes(true);
      fetchAttributes(effectiveProjectId).finally(() => setIsLoadingAttributes(false));
    }
  }, [projectId, flight?.project_id, fetchAttributes]);

  // Load flight's current attributes in edit mode
  useEffect(() => {
    if (flight?.id) {
      getFlightAttributes(flight.id).then(attrs => {
        setSelectedAttributeIds(attrs.map(a => a.id));
      }).catch(() => {
        // Use attributes from flight object as fallback
        if (flight.attributes) {
          setSelectedAttributeIds(flight.attributes.map(a => a.id));
        }
      });
    }
  }, [flight?.id, flight?.attributes, getFlightAttributes]);

  // Toggle attribute selection (max 4)
  const handleAttributeToggle = (attributeId: string) => {
    setSelectedAttributeIds(prev => {
      if (prev.includes(attributeId)) {
        return prev.filter(id => id !== attributeId);
      }
      if (prev.length >= 4) {
        return prev; // Max 4 attributes
      }
      return [...prev, attributeId];
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const baseData = {
      name: formData.name,
      description: formData.description || undefined,
      start_date: formData.start_date,
      end_date: formData.end_date,
      ai_context: {
        brand_voice: formData.brand_voice || undefined,
        target_audience: formData.target_audience || undefined,
        key_messages: formData.key_messages ? formData.key_messages.split(',').map(s => s.trim()) : undefined,
        hashtag_strategy: formData.hashtags ? formData.hashtags.split(/[\s,]+/).filter(Boolean) : undefined,
      },
      goals: {
        post_count: formData.post_count ? parseInt(formData.post_count) : undefined,
        platforms: formData.platforms.length > 0 ? formData.platforms : undefined,
      },
      color: formData.color,
    };

    try {
      if (isCreateMode) {
        if (!projectId) {
          throw new Error(t('projectIdMissing'));
        }
        const newFlight = await createFlight({
          ...baseData,
          project_id: projectId,
        });
        // Set attributes for new flight
        if (selectedAttributeIds.length > 0) {
          await setFlightAttributes(newFlight.id, selectedAttributeIds);
        }
        onCreated?.(newFlight);
      } else {
        await updateFlight(flight.id, {
          ...baseData,
          status: formData.status,
        });
        // Update attributes
        await setFlightAttributes(flight.id, selectedAttributeIds);
        onUpdated?.();
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errorOccurred'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePlatformToggle = (platform: string) => {
    setFormData(prev => ({
      ...prev,
      platforms: prev.platforms.includes(platform)
        ? prev.platforms.filter(p => p !== platform)
        : [...prev.platforms, platform],
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: formData.color }}>
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </span>
              {isCreateMode ? t('createFlight') : t('editFlight')}
            </h2>
            {flight && <p className="text-sm text-gray-500 mt-0.5">{flight.display_id}</p>}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Basic Info */}
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('name')} *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('placeholder.name')}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('description')}</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={2}
                placeholder={t('placeholder.description')}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('startDate')} *</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('endDate')} *</label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>

            {/* Status (only in edit mode) */}
            {!isCreateMode && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('filters.status')}</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as FlightStatus }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="draft">{t('status.draft')}</option>
                  <option value="active">{t('status.active')}</option>
                  <option value="paused">{t('status.paused')}</option>
                  <option value="completed">{t('status.completed')}</option>
                  <option value="archived">{t('status.archived')}</option>
                </select>
              </div>
            )}

            {/* Color Picker */}
            <div>
              <ColorPicker
                color={formData.color}
                onChange={(color) => setFormData(prev => ({ ...prev, color }))}
                label={t('color')}
              />
            </div>

            {/* Attribute Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('attributes')} <span className="text-gray-400 font-normal">({t('attributesOptional')})</span>
              </label>
              {isLoadingAttributes ? (
                <div className="text-sm text-gray-500">{t('loadingAttributes')}</div>
              ) : attributes.length === 0 ? (
                <div className="text-sm text-gray-500">{t('noAttributesAvailable')}</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {attributes.map((attr) => {
                    const isSelected = selectedAttributeIds.includes(attr.id);
                    const isDisabled = !isSelected && selectedAttributeIds.length >= 4;
                    return (
                      <button
                        key={attr.id}
                        type="button"
                        onClick={() => handleAttributeToggle(attr.id)}
                        disabled={isDisabled}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          isSelected
                            ? 'ring-2 ring-offset-1'
                            : isDisabled
                              ? 'opacity-40 cursor-not-allowed'
                              : 'hover:ring-1 hover:ring-offset-1'
                        }`}
                        style={{
                          backgroundColor: isSelected ? attr.color : `${attr.color}20`,
                          color: isSelected ? 'white' : attr.color,
                          ['--tw-ring-color' as string]: attr.color,
                        }}
                      >
                        {attr.is_system && (
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                        )}
                        {attr.name}
                        {isSelected && (
                          <svg className="w-3 h-3 ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              {selectedAttributeIds.length >= 4 && (
                <p className="text-xs text-amber-600 mt-1">{t('attributesMax')}</p>
              )}
            </div>
          </div>

          {/* AI Context */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
              <span className="text-lg">🤖</span> {t('aiContext')}
            </h3>
            <p className="text-xs text-gray-500 mb-3">
              {t('aiContextDescription')}
            </p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('brandVoice')}</label>
                <input
                  type="text"
                  value={formData.brand_voice}
                  onChange={(e) => setFormData(prev => ({ ...prev, brand_voice: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('placeholder.brandVoice')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('targetAudience')}</label>
                <input
                  type="text"
                  value={formData.target_audience}
                  onChange={(e) => setFormData(prev => ({ ...prev, target_audience: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('placeholder.targetAudience')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('keyMessages')} ({t('keyMessagesHint')})</label>
                <input
                  type="text"
                  value={formData.key_messages}
                  onChange={(e) => setFormData(prev => ({ ...prev, key_messages: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('placeholder.keyMessages')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('hashtagStrategy')}</label>
                <input
                  type="text"
                  value={formData.hashtags}
                  onChange={(e) => setFormData(prev => ({ ...prev, hashtags: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('placeholder.hashtags')}
                />
              </div>
            </div>
          </div>

          {/* Goals */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <span className="text-lg">🎯</span> {t('goals')}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('plannedPostCount')}</label>
                <input
                  type="number"
                  value={formData.post_count}
                  onChange={(e) => setFormData(prev => ({ ...prev, post_count: e.target.value }))}
                  className="w-32 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min="1"
                  placeholder={t('placeholder.postCount')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('platforms')}</label>
                <div className="flex gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.platforms.includes('facebook')}
                      onChange={() => handlePlatformToggle('facebook')}
                      className="mr-2 h-4 w-4 text-blue-600 rounded"
                    />
                    <span className="text-sm">Facebook</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.platforms.includes('instagram')}
                      onChange={() => handlePlatformToggle('instagram')}
                      className="mr-2 h-4 w-4 text-pink-600 rounded"
                    />
                    <span className="text-sm">Instagram</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-50"
            >
              {t('common:cancel', 'Abbrechen')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formData.name}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? t('saving') : isCreateMode ? t('createFlight') : t('saveChanges')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditFlightModal;
